import React from "react";
import { useSearchParams } from "react-router-dom";
import PanelDashboardLayout from "../PanelDashboardLayout";
import OverviewTab from "./tabs/OverviewTab";
import FeeStructuresTab from "./tabs/FeeStructuresTab";
import PaymentsTab from "./tabs/PaymentsTab";
import ReceiptsTab from "./tabs/ReceiptsTab";
import ManualPaymentsTab from "./tabs/ManualPaymentsTab";
import InstallmentSettingsTab from "./tabs/InstallmentSettingsTab";

// Consolidated admin Fees page. Route: /panel-admin/fees
//
// Replaces six sprawling admin fee pages + the dead FeeDashboard, collapsing
// duplicated fee-structure CRUD, overlapping receipt viewers and the two
// pending-payments screens into one tabbed surface. The active tab lives in the
// URL query (?tab=...) so tabs are linkable/back-button friendly.

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "structures", label: "Fee Structures" },
  { key: "payments", label: "Payments" },
  { key: "receipts", label: "Receipts" },
  { key: "manual", label: "Manual Payments" },
  { key: "installments", label: "Installment Settings" },
];

const DEFAULT_TAB = "overview";

const FeesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.key === requested) ? requested : DEFAULT_TAB;

  const changeTab = (key) => {
    // Merge into existing params so we don't nuke other query keys.
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next);
  };

  const renderTab = () => {
    switch (activeTab) {
      case "structures":
        return <FeeStructuresTab />;
      case "payments":
        return <PaymentsTab />;
      case "receipts":
        return <ReceiptsTab />;
      case "manual":
        return <ManualPaymentsTab />;
      case "installments":
        return <InstallmentSettingsTab />;
      case "overview":
      default:
        return <OverviewTab onNavigateTab={changeTab} />;
    }
  };

  return (
    <PanelDashboardLayout>
      <div className="max-w-7xl mx-auto py-6 px-4">
        {/* Page heading */}
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Fees</h1>
          <p className="text-gray-600 mt-1">
            Structures, payments, receipts and installment settings in one place.
          </p>
        </div>

        {/* Tab bar */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex flex-wrap gap-1 sm:gap-2" aria-label="Fees tabs">
            {TABS.map((tab) => {
              const isActive = tab.key === activeTab;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => changeTab(tab.key)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Active tab */}
        {renderTab()}
      </div>
    </PanelDashboardLayout>
  );
};

export default FeesPage;
